import {
	ActiveEnvelopeResponse,
	BrokerCommunication,
	PaginatedPropositions,
	Proposition,
	PropositionDetails,
	PropositionDocument,
	PropositionFiltersDto,
	ProjectDocumentForSignatureTypeKeys,
	ProjectDocumentForSignatureType,
	Task,
} from "@dune/types";
import { useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { propositionsQueryKeys } from "../query-keys/propositions.query-keys";

export const getPropositions = async (
	filters?: PropositionFiltersDto,
): Promise<PaginatedPropositions> => {
	const { data } = await privateAxios.get<PaginatedPropositions>(
		"/propositions",
		{
			params: filters,
		},
	);
	return data;
};

export const getProposition = async (
	id: number,
): Promise<PropositionDetails> => {
	const { data } = await privateAxios.get<PropositionDetails>(
		`/propositions/${id}`,
	);
	return data;
};

export const getPropositionDocuments = async (
	propositionId: number,
): Promise<PropositionDocument[]> => {
	const { data } = await privateAxios.get<PropositionDocument[]>(
		`/propositions/${propositionId}/documents`,
	);
	return data;
};

export const getPropositionActiveEnvelope = async (
	propositionId: number,
	type: ProjectDocumentForSignatureTypeKeys,
): Promise<ActiveEnvelopeResponse> => {
	const { data } = await privateAxios.get<ActiveEnvelopeResponse>(
		`/propositions/${propositionId}/active-${ProjectDocumentForSignatureType[type]}-envelope`,
	);
	return data;
};

export const getPropositionsByConstructionSite = async (
	constructionSiteId: number,
): Promise<Proposition[]> => {
	const { data } = await privateAxios.get<Proposition[]>(
		`/construction-sites/${constructionSiteId}/propositions`,
	);
	return data;
};

export const getPropositionFiles = async (
	propositionId: number,
	type?: string,
) => {
	const params = type ? { type } : {};
	const { data } = await privateAxios.get<
		{
			id: number;
			createdAt: string;
			file: {
				id: string;
				name: string;
				mimeType: string;
				key: string;
				size: number;
				url: string;
			};
			type: string;
		}[]
	>(`/propositions/${propositionId}/files`, { params });
	return data;
};

export const getPropositionsByBroker = async (
	brokerId: number,
): Promise<Proposition[]> => {
	const { data } = await privateAxios.get<Proposition[]>(
		`/societies/broker/${brokerId}/propositions`,
	);
	return data;
};

export const getPropositionCommunications = async (
	propositionId: number,
	params?: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
) => {
	const { data } = await privateAxios.get<{
		data: BrokerCommunication[];
		meta: {
			page: number;
			limit: number;
			total: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	}>(`/propositions/${propositionId}/communications`, {
		params,
	});
	return data;
};

export const getPropositionTasks = async (propositionId: number) => {
	const { data } = await privateAxios.get<Task[]>(
		`/propositions/${propositionId}/tasks`,
	);
	return data;
};

export const getPropositionUnprocessedCommunicationsCount = async (
	propositionId: number,
): Promise<number> => {
	const { data } = await privateAxios.get<number>(
		`/propositions/${propositionId}/communications/unprocessed-count`,
	);
	return data;
};

export const usePropositions = (filters?: PropositionFiltersDto) => {
	return useQuery({
		queryKey: propositionsQueryKeys.list(filters),
		queryFn: () => getPropositions(filters),
		staleTime: 30 * 1000,
	});
};

export const useProposition = (id: number) => {
	return useQuery({
		queryKey: propositionsQueryKeys.detail(id),
		queryFn: () => getProposition(id),
		enabled: !!id,
	});
};

export const usePropositionDocuments = (propositionId: number) => {
	return useQuery({
		queryKey: propositionsQueryKeys.documents(propositionId),
		queryFn: () => getPropositionDocuments(propositionId),
		enabled: !!propositionId,
	});
};

export const usePropositionActiveEnvelope = (
	propositionId: number,
	type: ProjectDocumentForSignatureTypeKeys,
) => {
	return useQuery({
		queryKey: propositionsQueryKeys.activeEnvelope(propositionId),
		queryFn: () => getPropositionActiveEnvelope(propositionId, type),
		enabled: !!propositionId,
	});
};

export const usePropositionsByConstructionSite = (
	constructionSiteId: number,
) => {
	return useQuery({
		queryKey: propositionsQueryKeys.byConstructionSite(constructionSiteId),
		queryFn: () => getPropositionsByConstructionSite(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

export const usePropositionsByBroker = (brokerId: number) => {
	return useQuery({
		queryKey: propositionsQueryKeys.byBroker(brokerId),
		queryFn: () => getPropositionsByBroker(brokerId),
		enabled: !!brokerId,
	});
};

export const usePropositionCommunications = (
	propositionId: number,
	params?: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: propositionsQueryKeys.communications(propositionId, params),
		queryFn: () => getPropositionCommunications(propositionId, params),
		enabled: !!propositionId && options?.enabled !== false,
	});
};

export const useGetPropositionFiles = (
	propositionId: number,
	type?: string,
) => {
	return useQuery({
		queryKey: propositionsQueryKeys.files(propositionId, type),
		queryFn: () => getPropositionFiles(propositionId, type),
		enabled: !!propositionId,
	});
};

export const usePropositionTasks = (
	propositionId: number,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: propositionsQueryKeys.tasks(propositionId),
		queryFn: () => getPropositionTasks(propositionId),
		enabled: !!propositionId && options?.enabled !== false,
	});
};

export const usePropositionUnprocessedCommunicationsCount = (
	propositionId: number,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey:
			propositionsQueryKeys.unprocessedCommunicationsCount(propositionId),
		queryFn: () => getPropositionUnprocessedCommunicationsCount(propositionId),
		enabled: !!propositionId && options?.enabled !== false,
		select: (data) => data ?? 0,
	});
};

export const getPropositionsCounts = async (): Promise<{
	statusCounts: Record<string, number>;
	productTypeCounts: Record<string, number>;
}> => {
	const { data } = await privateAxios.get<{
		statusCounts: Record<string, number>;
		productTypeCounts: Record<string, number>;
	}>("/propositions/counts");
	return data;
};

export const usePropositionsCounts = () => {
	return useQuery({
		queryKey: propositionsQueryKeys.counts(),
		queryFn: () => getPropositionsCounts(),
		staleTime: 30 * 1000,
	});
};

export const getPropositionForTemplate = async (
	propositionId: number,
): Promise<Record<string, unknown>> => {
	const { data } = await privateAxios.get<Record<string, unknown>>(
		`/propositions/${propositionId}/template-data`,
	);
	return data;
};

export const usePropositionForTemplate = (
	propositionId: number,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: propositionsQueryKeys.templateData(propositionId),
		queryFn: () => getPropositionForTemplate(propositionId),
		enabled: !!propositionId && (options?.enabled !== false),
	});
};
